/*
 * inselvolt Kompatibilitätsberater — Preisquelle zur Laufzeit.
 *
 * Drei Ebenen, in dieser Reihenfolge:
 *   1. Storefront-API per GraphQL — eine Abfrage für alles, optional mit
 *      Lagerbeständen. Braucht ein (öffentlich-sicheres) Token.
 *   2. Shopifys eigener Produkt-Endpunkt /products/<handle>.js — ohne jeden
 *      Zugang, funktioniert aber nur, wenn die Seite IM Shop läuft.
 *   3. Die gespeicherte Datei daten/preise.js.
 * Greift Ebene 3, sagt die UI das sichtbar samt Datum. Ein stiller Rückfall auf
 * alte Preise wäre die schlechteste Variante: Der Kunde sähe einen falschen
 * Preis, ohne es zu erfahren.
 *
 * WARUM STOREFRONT-API UND NICHT ADMIN-API:
 * Die Admin-API verlangt ein Token mit Schreibrechten auf den ganzen Shop. Ein
 * solches Token in einer Seite auszuliefern heißt, es zu veröffentlichen — jeder
 * Besucher könnte damit den Shop-Admin lesen und ändern. Die Storefront-API ist
 * genau für den umgekehrten Fall gebaut: Ihr Token ist öffentlich-sicher und darf
 * ausschließlich lesen, was ohnehin im Schaufenster steht. Die Admin-API nutzen
 * wir deshalb serverseitig im Tagesabgleich (tools/preise-generieren.js --graphql),
 * wo sie zusätzlich Lagerbestände und Metafelder liefern kann.
 *
 * Die gespeicherte Datei bleibt außerdem die ZUORDNUNGSTABELLE: Nur Produkte und
 * Varianten, die dort einer Familie/Größe zugeordnet sind, bekommen einen Preis.
 * Der Live-Abruf aktualisiert diese Einträge, er erfindet keine neuen — welche
 * Shop-Variante zu welchem Datensatz gehört, bleibt eine kuratierte Entscheidung
 * (MAPPING in tools/preise-generieren.js).
 *
 * Konfiguration (optional; ohne Token greift Ebene 2, außerhalb des Shops Ebene 3):
 *   window.IV_BERATER_KONFIG = {
 *     shop_domain: 'inselvolt.de',
 *     storefront_token: '…',        // öffentlicher Storefront-API-Token
 *     storefront_version: '2026-04',
 *     zeitlimit_ms: 4000,
 *   };
 */
(function (root) {
  'use strict';

  var STANDARD = {
    shop_domain: null,
    storefront_token: null,
    storefront_version: '2026-04',
    zeitlimit_ms: 4000,
  };

  function konfig() {
    var k = (root && root.IV_BERATER_KONFIG) || {};
    return {
      shop_domain: k.shop_domain || STANDARD.shop_domain,
      storefront_token: k.storefront_token || STANDARD.storefront_token,
      storefront_version: k.storefront_version || STANDARD.storefront_version,
      zeitlimit_ms: typeof k.zeitlimit_ms === 'number' ? k.zeitlimit_ms : STANDARD.zeitlimit_ms,
    };
  }

  function handlesVon(preise) {
    var gesehen = {};
    var liste = [];
    (preise && preise.eintraege ? preise.eintraege : []).forEach(function (e) {
      if (e.handle && !gesehen[e.handle]) { gesehen[e.handle] = true; liste.push(e.handle); }
    });
    return liste;
  }

  /*
   * Eine Abfrage für alle Produkte: je Handle ein Alias mit eigener Variablen —
   * Handles werden nie in den Query-Text eingesetzt (keine Injektion, keine
   * Escaping-Fehler bei Sonderzeichen).
   */
  function baueAbfrage(handles) {
    var kopf = handles.map(function (_, i) { return '$h' + i + ': String!'; }).join(', ');
    var felder = handles.map(function (_, i) {
      return 'p' + i + ': productByHandle(handle: $h' + i + ') { handle title variants(first: 100) '
        + '{ nodes { id title availableForSale quantityAvailable price { amount currencyCode } } } }';
    }).join('\n  ');
    var variablen = {};
    handles.forEach(function (h, i) { variablen['h' + i] = h; });
    return { query: 'query BeraterPreise(' + kopf + ') {\n  ' + felder + '\n}', variables: variablen };
  }

  function cents(betrag) {
    var zahl = parseFloat(betrag);
    if (!isFinite(zahl)) return null;
    return Math.round(zahl * 100);
  }

  /*
   * Live-Werte auf die gepflegten Einträge legen. Nicht gefundene Produkte oder
   * Varianten behalten den Dateiwert — besser ein datierter alter Preis als gar
   * keiner. Ein Preis ≤ 0 wird verworfen (die Engine würde ihn ohnehin ignorieren).
   */
  function zahlId(wert) {
    if (wert === null || wert === undefined) return null;
    var n = Number(String(wert).replace(/^.*\//, ''));
    return isFinite(n) && n > 0 ? n : null;
  }

  function verschmelzen(preise, produkte) {
    var proHandle = {};
    Object.keys(produkte).forEach(function (alias) {
      var p = produkte[alias];
      if (!p || !p.handle) return;
      var nachTitel = {};
      var nachId = {};
      ((p.variants && p.variants.nodes) || []).forEach(function (v) {
        nachTitel[v.title] = v;
        var id = zahlId(v.id);
        if (id) nachId[id] = v;
      });
      proHandle[p.handle] = { titel: nachTitel, id: nachId };
    });

    var aktualisiert = 0;
    var eintraege = preise.eintraege.map(function (e) {
      var eintrag = proHandle[e.handle];
      if (!eintrag) return e;
      /*
       * Zuerst über die Varianten-ID: Ein Variantentitel kann im Shop
       * umbenannt werden, die ID bleibt. Der Titelvergleich ist nur der
       * Rückfall für Einträge, die noch keine ID tragen.
       */
      var variante = (e.variante_id && eintrag.id[zahlId(e.variante_id)])
        || eintrag.titel[e.shop_variante];
      if (!variante) return e;
      var neuerPreis = variante.price ? cents(variante.price.amount) : null;
      if (neuerPreis === null || neuerPreis <= 0) return e;
      aktualisiert += 1;
      var kopie = {};
      for (var k in e) if (Object.prototype.hasOwnProperty.call(e, k)) kopie[k] = e[k];
      kopie.preis_cents = neuerPreis;
      kopie.verfuegbar = variante.availableForSale !== false;
      if (typeof variante.quantityAvailable === 'number') kopie.bestand = variante.quantityAvailable;
      return kopie;
    });

    return {
      preise: { quelle: preise.quelle, eintraege: eintraege },
      aktualisiert: aktualisiert,
    };
  }

  /*
   * Zweiter Live-Weg, ganz ohne Zugangsdaten: Läuft die Seite im Shop selbst,
   * liegt Shopifys eigene Storefront-Schnittstelle auf derselben Domain —
   * /products/<handle>.js liefert je Variante Preis (in Cent) und
   * Verfügbarkeit. Kein Token, keine App, keine Berechtigung.
   *
   * Warum trotzdem die Storefront-API als erste Wahl: Sie holt alles in EINER
   * Abfrage und kann zusätzlich Lagerbestände liefern. Dieser Weg braucht eine
   * Anfrage je Produkt (derzeit 32) — deshalb in kleinen Gruppen, damit der
   * Browser nicht alles gleichzeitig aufmacht.
   *
   * Fremde Domains gehen hier bewusst nicht: Der Endpunkt schickt keine
   * CORS-Freigabe. Außerhalb des Shops scheitert der Abruf und die Datei greift
   * — genau wie vorgesehen.
   */
  var GRUPPE = 6;

  function ajaxProdukt(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (antwort) {
        if (!antwort.ok) throw new Error('HTTP ' + antwort.status);
        return antwort.json();
      })
      .then(function (produkt) {
        // Auf die Form der GraphQL-Antwort bringen, damit es nur EINE
        // Zusammenführung gibt statt zweier, die auseinanderlaufen können.
        return {
          handle: produkt.handle || handle,
          variants: {
            nodes: (produkt.variants || []).map(function (v) {
              return {
                id: v.id || null,
                title: v.title,
                price: { amount: String((v.price || 0) / 100) },
                availableForSale: v.available !== false,
              };
            }),
          },
        };
      })
      .catch(function () { return null; });
  }

  function ueberShopEndpunkt(handles) {
    var ergebnis = {};
    var i = 0;
    function naechsteGruppe() {
      if (i >= handles.length) return Promise.resolve(ergebnis);
      var start = i;
      var teil = handles.slice(start, start + GRUPPE);
      i += GRUPPE;
      return Promise.all(teil.map(ajaxProdukt)).then(function (produkte) {
        produkte.forEach(function (p, n) { if (p) ergebnis['p' + (start + n)] = p; });
        return naechsteGruppe();
      });
    }
    return naechsteGruppe();
  }

  function mitZeitlimit(versprechen, ms, abbruch) {
    return new Promise(function (aufloesen, ablehnen) {
      var timer = setTimeout(function () {
        if (abbruch) { try { abbruch.abort(); } catch (fehler) { /* egal */ } }
        ablehnen(new Error('Zeitlimit von ' + ms + ' ms überschritten'));
      }, ms);
      versprechen.then(function (wert) { clearTimeout(timer); aufloesen(wert); },
        function (fehler) { clearTimeout(timer); ablehnen(fehler); });
    });
  }

  /*
   * Die GraphQL-Antwort auswerten — als reine Funktion, damit der Fehlerpfad
   * testbar ist. Wirft; laden() fängt das, fällt sichtbar auf die Datei
   * zurück (Kunden sehen den generischen Ausfalltext der Herkunftszeile)
   * und schreibt den technischen Grund in `meldung` und die Konsole.
   * Häufigster Konfigurationsfehler: ein Token ohne den Bestands-Scope
   * unauthenticated_read_product_inventory. Den nennt die Meldung beim Namen,
   * damit die Behebung nicht an der englischen Rohmeldung hängenbleibt.
   */
  function antwortAuswerten(json, basis) {
    if (json && json.errors && json.errors.length) {
      var text = String(json.errors[0].message || 'GraphQL-Fehler');
      if (/quantityAvailable|read_product_inventory/i.test(text)) {
        text = 'Storefront-Token ohne Bestands-Freigabe '
          + 'unauthenticated_read_product_inventory — ' + text;
      }
      throw new Error(text);
    }
    if (!json || !json.data) throw new Error('Antwort ohne Daten');
    var verschmolzen = verschmelzen(basis, json.data);
    if (!verschmolzen.aktualisiert) throw new Error('Antwort enthielt keinen passenden Preis');
    return verschmolzen;
  }

  /*
   * Liefert immer ein Ergebnis — nie eine abgelehnte Zusage. Die UI soll nie
   * wegen eines Preisabrufs kaputtgehen (graceful degradation, CLAUDE.md §5).
   *
   *   { preise, quelle: 'live' | 'datei', stand, meldung, aktualisiert }
   */
  function laden(dateiPreise) {
    var basis = dateiPreise || (root && root.IVBeraterPreise) || { quelle: {}, eintraege: [] };
    var ausDatei = function (meldung) {
      return {
        preise: basis,
        quelle: 'datei',
        stand: basis.quelle ? basis.quelle.abruf_datum : null,
        meldung: meldung,
        aktualisiert: 0,
      };
    };

    var k = konfig();
    if (typeof fetch !== 'function') {
      return Promise.resolve(ausDatei('Browser unterstützt den Live-Abruf nicht'));
    }

    var handles = handlesVon(basis);
    if (!handles.length) return Promise.resolve(ausDatei('keine zugeordneten Shop-Produkte'));

    // Ohne Token: der tokenfreie Weg über den Shop selbst.
    if (!k.storefront_token || !k.shop_domain) {
      var ajax = ueberShopEndpunkt(handles).then(function (produkte) {
        var v = verschmelzen(basis, produkte);
        if (!v.aktualisiert) throw new Error('Shop lieferte keinen passenden Preis');
        return { preise: v.preise, quelle: 'live', stand: null, meldung: null, aktualisiert: v.aktualisiert };
      });
      return mitZeitlimit(ajax, k.zeitlimit_ms, null)
        .catch(function () { return ausDatei('kein Live-Zugang hinterlegt'); });
    }

    var abfrage = baueAbfrage(handles);
    var url = 'https://' + k.shop_domain + '/api/' + k.storefront_version + '/graphql.json';
    var abbruch = typeof AbortController === 'function' ? new AbortController() : null;

    var anfrage = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': k.storefront_token,
      },
      body: JSON.stringify(abfrage),
      signal: abbruch ? abbruch.signal : undefined,
    }).then(function (antwort) {
      if (!antwort.ok) throw new Error('Shop antwortet mit HTTP ' + antwort.status);
      return antwort.json();
    }).then(function (json) {
      var verschmolzen = antwortAuswerten(json, basis);
      return {
        preise: verschmolzen.preise,
        quelle: 'live',
        stand: null,
        meldung: null,
        aktualisiert: verschmolzen.aktualisiert,
      };
    });

    return mitZeitlimit(anfrage, k.zeitlimit_ms, abbruch)
      .catch(function (fehler) {
        var meldung = fehler && fehler.message ? fehler.message : 'Abruf fehlgeschlagen';
        /* Hier ist ein Token konfiguriert — ein Scheitern ist immer eine
         * Störung oder Fehlkonfiguration, nie Normalbetrieb. Der technische
         * Grund gehört in die Konsole; die Herkunftszeile bleibt für Kunden
         * beim generischen Text. (Die tokenlosen Wege warnen bewusst nicht:
         * außerhalb des Shops ist der Rückfall dort der vorgesehene Weg.) */
        try { console.warn('Preisquelle: Live-Abruf gescheitert — ' + meldung); } catch (egal) { /* egal */ }
        return ausDatei(meldung);
      });
  }

  var API = {
    laden: laden,
    baueAbfrage: baueAbfrage,
    verschmelzen: verschmelzen,
    ueberShopEndpunkt: ueberShopEndpunkt,
    antwortAuswerten: antwortAuswerten,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.IVPreisQuelle = API;
})(typeof window !== 'undefined' ? window : null);
